/**
 * Custom hooks per Document Schemas
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type { DocumentSchemaCreate, DocumentSchemaListFilters, DocumentSchemaResponse, DocumentSchemaUpdate } from "@/types/api/document-schemas";

const DOCUMENT_SCHEMAS_QUERY_KEYS = {
    all: ["document-schemas"] as const,
    lists: () => [...DOCUMENT_SCHEMAS_QUERY_KEYS.all, "list"] as const,
    list: (filters?: DocumentSchemaListFilters) => [...DOCUMENT_SCHEMAS_QUERY_KEYS.lists(), filters] as const,
    details: () => [...DOCUMENT_SCHEMAS_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...DOCUMENT_SCHEMAS_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook per lista document schemas con filtri
 */
export function useDocumentSchemas(
    filters?: DocumentSchemaListFilters,
    options?: Omit<UseQueryOptions<DocumentSchemaResponse[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<DocumentSchemaResponse[]> => {
            return await apiGet<DocumentSchemaResponse[]>("/document-schemas", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook per singolo document schema
 */
export function useDocumentSchema(schemaId: string) {
    return useQuery({
        queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.detail(schemaId),
        queryFn: async (): Promise<DocumentSchemaResponse> => {
            return await apiGet<DocumentSchemaResponse>(`/document-schemas/${schemaId}`);
        },
        enabled: !!schemaId,
    });
}

/**
 * Hook per creare document schema
 */
export function useCreateDocumentSchema() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: DocumentSchemaCreate): Promise<DocumentSchemaResponse> => {
            return await apiPost<DocumentSchemaResponse>("/document-schemas", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per aggiornare document schema
 */
export function useUpdateDocumentSchema() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ schemaId, data }: { schemaId: string; data: DocumentSchemaUpdate }): Promise<DocumentSchemaResponse> => {
            return await apiPut<DocumentSchemaResponse>(`/document-schemas/${schemaId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.detail(variables.schemaId) });
        },
    });
}

/**
 * Hook per eliminare document schema
 */
export function useDeleteDocumentSchema() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (schemaId: string): Promise<void> => {
            return await apiDelete<void>(`/document-schemas/${schemaId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_SCHEMAS_QUERY_KEYS.lists() });
        },
    });
}
